import { fieldTypeOf } from '../field-types';
import type { Element, FieldElement, FormDefinition, RepeatableGroupField, SectionElement } from './form-definition';
import { hasOptions } from '../field-types/card';
import { holdsFields } from './elements';

type ReferenceKind = 'visibleWhen' | 'validationWhen' | 'bound';

export type ReferenceProblem = 'missing' | 'notAField' | 'self' | 'inside' | 'container' | 'intoGroup' | 'below';

interface Place {
  element: Element;
  index: number;
  section?: SectionElement;
  group?: RepeatableGroupField;
}

export type Places = Map<string, Place>;

export const placesOf = (definition: FormDefinition): Places => {
  const places = new Map<string, Place>();
  const visit = (elements: Element[], section?: SectionElement, group?: RepeatableGroupField) => {
    for (const element of elements) {
      places.set(element.id, { element, index: places.size, section, group });
      if (element.kind === 'section') visit(element.elements, element, group);
      else if (element.kind === 'field' && holdsFields(element)) visit(element.elements, section, element);
    }
  };
  visit(definition.elements);
  return places;
};

type ReferenceTarget = { field: FieldElement; problem?: undefined } | { field?: undefined; problem: ReferenceProblem };

export const referenceTarget = (places: Places, fromId: string, toId: string, kind: ReferenceKind): ReferenceTarget => {
  const to = places.get(toId);
  if (!to) return { problem: 'missing' };
  if (to.element.kind !== 'field') return { problem: 'notAField' };
  if (toId === fromId) return { problem: 'self' };
  const from = places.get(fromId);
  if (from) {
    if (to.section?.id === fromId || to.group?.id === fromId) return { problem: 'inside' };
    if (from.group?.id === toId) return { problem: 'container' };
    if (to.group && to.group.id !== from.group?.id) return { problem: 'intoGroup' };
    if (kind === 'visibleWhen' && to.index > from.index) return { problem: 'below' };
  }
  return { field: to.element };
};

export const sameKindOfValue = (field: FieldElement, other: FieldElement): boolean => {
  const kind = fieldTypeOf(field).valueKind;
  const otherKind = fieldTypeOf(other).valueKind;
  if (hasOptions(kind)) return hasOptions(otherKind);
  return kind === otherKind && (kind === 'text' || kind === 'number' || kind === 'date');
};

export const mayBeBoundOf = (field: FieldElement, other: FieldElement): boolean => {
  const kind = fieldTypeOf(field).valueKind;
  return (kind === 'number' || kind === 'date') && fieldTypeOf(other).valueKind === kind;
};

interface ReferableField {
  field: FieldElement;
  problem?: ReferenceProblem;
  section?: SectionElement;
}

export const referableFields = (definition: FormDefinition, fromId: string, kind: ReferenceKind): ReferableField[] => {
  const places = placesOf(definition);
  return [...places.values()].flatMap(({ element, section }) =>
    element.kind === 'field'
      ? [{ field: element, problem: referenceTarget(places, fromId, element.id, kind).problem, section }]
      : [],
  );
};
